import React, { useRef, useEffect, useCallback } from 'react';

import cn from 'classnames';
import { debounce } from 'lodash';

import * as styles from './Tabs.module.scss';

interface TabsProps {
  tabs: string[];
  activeTab?: number;
  onTabChange: (index: number) => void;
}

const Tabs: React.FC<TabsProps> = ({ tabs, activeTab = 0, onTabChange }) => {
  const tabRef = useRef<(HTMLButtonElement | null)[]>([]);
  const indicatorRef = useRef<HTMLDivElement>(null);

  const handleTabClick = (index: number) => {
    onTabChange(index);
  };

  const adjustIndicator = useCallback(() => {
    const indicator = indicatorRef.current;
    const activeTabElement = tabRef.current[activeTab];
    if (!activeTabElement || !indicator) {
      return;
    }
    const tabWidth = activeTabElement.offsetWidth;
    const tabOffset = activeTabElement.offsetLeft;
    if (indicator) {
      indicator.style.transform = `translateX(${tabOffset}px)`;
      indicator.style.width = `${tabWidth}px`;
    }
  }, [activeTab]);

  useEffect(() => {
    adjustIndicator();
    const debouncedAdjustIndicator = debounce(adjustIndicator, 100);
    window.addEventListener('resize', debouncedAdjustIndicator);
    return () => {
      window.removeEventListener('resize', debouncedAdjustIndicator);
    };
  }, [adjustIndicator]);

  return (
    <div className={styles.tabs}>
      {tabs.map((tab, index) => (
        <button
          key={index}
          className={cn(styles.tab, activeTab === index ? styles.active : '')}
          onClick={() => (activeTab !== index ? handleTabClick(index) : null)}
          ref={(el) => (tabRef.current[index] = el)}
        >
          {tab}
        </button>
      ))}
      <div className={styles.indicator} ref={indicatorRef} />
    </div>
  );
};

export default Tabs;
